import React, { useEffect, useState, useCallback, createContext, useContext } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, AlertTriangle, Info, X } from 'lucide-react';
import { T, springs } from '../../lib/tokens';

const ToastContext = createContext(null);

const typeConfig = {
  success: { icon: Check, color: T.green, bg: 'rgba(90,158,120,0.10)', border: 'rgba(90,158,120,0.20)' },
  warning: { icon: AlertTriangle, color: T.amber, bg: 'rgba(212,146,30,0.10)', border: 'rgba(212,146,30,0.20)' },
  info: { icon: Info, color: T.blue, bg: 'rgba(74,158,204,0.10)', border: 'rgba(74,158,204,0.20)' },
  error: { icon: AlertTriangle, color: T.red, bg: 'rgba(224,88,88,0.10)', border: 'rgba(224,88,88,0.20)' },
};

let toastId = 0;

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback(({ message, type = 'info', duration = 4000 }) => {
    const id = ++toastId;
    setToasts(prev => [...prev, { id, message, type }]);
    if (duration > 0) {
      setTimeout(() => {
        setToasts(prev => prev.filter(t => t.id !== id));
      }, duration);
    }
    return id;
  }, []);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ addToast, removeToast }}>
      {children}

      {/* Toast container */}
      <div style={{
        position: 'fixed', bottom: 24, right: 24,
        display: 'flex', flexDirection: 'column-reverse', gap: 8,
        zIndex: 1000, pointerEvents: 'none',
      }}>
        <AnimatePresence>
          {toasts.map(toast => {
            const config = typeConfig[toast.type] || typeConfig.info;
            const Icon = config.icon;
            return (
              <motion.div
                key={toast.id}
                initial={{ opacity: 0, x: 50, scale: 0.95 }}
                animate={{ opacity: 1, x: 0, scale: 1 }}
                exit={{ opacity: 0, x: 50, scale: 0.95 }}
                transition={springs.snappy}
                style={{
                  pointerEvents: 'auto',
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '12px 16px', borderRadius: 12,
                  background: 'rgba(255,255,255,0.95)',
                  backdropFilter: 'blur(20px) saturate(180%)',
                  WebkitBackdropFilter: 'blur(20px) saturate(180%)',
                  border: `1px solid ${config.border}`,
                  boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                  minWidth: 280,
                }}
              >
                <div style={{
                  width: 24, height: 24, borderRadius: 6,
                  background: config.bg, display: 'flex',
                  alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                  <Icon size={12} color={config.color} />
                </div>
                <span style={{ fontSize: 13, color: T.text, fontWeight: 500, flex: 1 }}>
                  {toast.message}
                </span>
                <button onClick={() => removeToast(toast.id)} style={{
                  border: 'none', background: 'none', cursor: 'pointer',
                  padding: 2, flexShrink: 0,
                }}>
                  <X size={12} color={T.textLight} />
                </button>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) throw new Error('useToast must be used within ToastProvider');
  return context;
}
